import { ApiError } from '../api';
import { toast } from 'sonner';
import {
  ErrorType,
  ErrorSeverity,
  type ErrorContext,
  type ProcessedError,
  type ErrorReport,
  type ProcessableError,
  type ErrorHandlerOptions,
} from '../types/error-types';

class ErrorService {
  private static instance: ErrorService;
  private errorQueue: ProcessedError[] = [];
  private maxQueueSize = 50;
  private reportingEnabled = true;

  static getInstance(): ErrorService {
    if (!ErrorService.instance) {
      ErrorService.instance = new ErrorService();
    }
    return ErrorService.instance;
  }

  // Main error processing method
  processError(
    error: ProcessableError,
    context?: ErrorContext
  ): ProcessedError {
    const processedError: ProcessedError = {
      id: this.generateErrorId(),
      type: this.categorizeError(error),
      severity: this.determineSeverity(error),
      message: this.extractMessage(error),
      originalError: error,
      context,
      timestamp: new Date().toISOString(),
      shouldRetry: this.shouldRetry(error),
      shouldReport: this.shouldReport(error),
      userMessage: this.getUserMessage(error),
      technicalMessage: this.getTechnicalMessage(error),
      suggestions: this.getSuggestions(error),
    };

    // Add to queue
    this.addToQueue(processedError);

    // Report if necessary
    if (processedError.shouldReport && this.reportingEnabled) {
      this.reportError(processedError);
    }

    return processedError;
  }

  // Handle error with UI feedback
  handleError(
    error: ProcessableError,
    context?: ErrorContext,
    options?: ErrorHandlerOptions
  ): ProcessedError {
    const { showToast = true, toastDuration, logToConsole = true } = options || {};
    
    const processedError = this.processError(error, context);

    // Log to console if enabled
    if (logToConsole) {
      this.logToConsole(processedError);
    }

    // Show toast notification if enabled
    if (showToast && !this.isValidationError(error)) {
      this.showToastNotification(processedError, toastDuration);
    }

    return processedError;
  }

  // Categorize error type
  private categorizeError(error: ProcessableError): ErrorType {
    if (typeof error === 'string') {
      return ErrorType.UNKNOWN;
    }

    if (error instanceof ApiError) {
      if (error.isNetworkError) return ErrorType.NETWORK;
      if (error.isUnauthorized) return ErrorType.AUTHENTICATION;
      if (error.isForbidden) return ErrorType.AUTHORIZATION;
      if (error.isValidationError) return ErrorType.VALIDATION;
      if (error.isServerError) return ErrorType.SERVER;
      if (error.isClientError) return ErrorType.CLIENT;
    }

    if (error instanceof Error) {
      // Check for common error patterns
      const message = error.message?.toLowerCase() || '';
      if (message.includes('network') || message.includes('fetch')) {
        return ErrorType.NETWORK;
      }
      if (message.includes('unauthorized') || message.includes('authentication')) {
        return ErrorType.AUTHENTICATION;
      }
      if (message.includes('forbidden') || message.includes('permission')) {
        return ErrorType.AUTHORIZATION;
      }
      if (message.includes('validation') || message.includes('invalid')) {
        return ErrorType.VALIDATION;
      }
    }

    return ErrorType.UNKNOWN;
  }

  // Determine error severity
  private determineSeverity(error: ProcessableError): ErrorSeverity {
    if (typeof error === 'string') {
      return ErrorSeverity.LOW;
    }

    if (error instanceof ApiError) {
      if (error.isServerError) return ErrorSeverity.HIGH;
      if (error.isUnauthorized || error.isForbidden) return ErrorSeverity.MEDIUM;
      if (error.isValidationError) return ErrorSeverity.LOW;
      if (error.isNetworkError) return ErrorSeverity.MEDIUM;
    }

    if (error instanceof Error) {
      // Check for critical patterns
      const message = error.message?.toLowerCase() || '';
      if (message.includes('critical') || message.includes('fatal')) {
        return ErrorSeverity.CRITICAL;
      }
    }

    return ErrorSeverity.MEDIUM;
  }

  // Extract error message
  private extractMessage(error: ProcessableError): string {
    if (typeof error === 'string') {
      return error;
    }
    if (error instanceof Error || error instanceof ApiError) {
      return error.message || 'Unknown error occurred';
    }
    return 'Unknown error occurred';
  }

  // Determine if error should be retried
  private shouldRetry(error: ProcessableError): boolean {
    if (typeof error === 'string') {
      return false;
    }

    if (error instanceof ApiError) {
      return error.isNetworkError || error.isServerError;
    }

    return false;
  }

  // Determine if error should be reported
  private shouldReport(error: ProcessableError): boolean {
    if (typeof error === 'string') {
      return false;
    }

    if (error instanceof ApiError) {
      // Don't report validation errors or client errors
      return !error.isValidationError && !error.isClientError;
    }

    if (error instanceof Error) {
      return true;
    }

    return false;
  }

  // Get user-friendly message
  private getUserMessage(error: ProcessableError): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof ApiError) {
      if (error.isNetworkError) {
        return 'Connection problem. Please check your internet connection.';
      }
      if (error.isUnauthorized) {
        return 'Your session has expired. Please log in again.';
      }
      if (error.isForbidden) {
        return 'You don\'t have permission to perform this action.';
      }
      if (error.isNotFound) {
        return 'The requested item was not found.';
      }
      if (error.isValidationError) {
        return 'Please check your input and try again.';
      }
      if (error.isServerError) {
        return 'Server error. Please try again later.';
      }
    }

    return 'An unexpected error occurred. Please try again.';
  }

  // Get technical message
  private getTechnicalMessage(error: ProcessableError): string {
    if (typeof error === 'string') {
      return error;
    }

    if (error instanceof ApiError) {
      return `${error.status ? `HTTP ${error.status}: ` : ''}${error.message}`;
    }

    if (error instanceof Error) {
      return error.message || 'Unknown error';
    }

    return 'Unknown error';
  }

  // Get error suggestions
  private getSuggestions(error: ProcessableError): string[] {
    const suggestions: string[] = [];

    if (typeof error === 'string') {
      suggestions.push('Try refreshing the page');
      return suggestions;
    }

    if (error instanceof ApiError) {
      if (error.isNetworkError) {
        suggestions.push('Check your internet connection');
        suggestions.push('Try again in a few moments');
        suggestions.push('Contact support if the problem persists');
      } else if (error.isUnauthorized) {
        suggestions.push('Log out and log back in');
        suggestions.push('Clear your browser cache');
      } else if (error.isForbidden) {
        suggestions.push('Contact your administrator for access');
        suggestions.push('Check if you have the required permissions');
      } else if (error.isNotFound) {
        suggestions.push('Check if the item still exists');
        suggestions.push('Try refreshing the page');
      } else if (error.isValidationError) {
        suggestions.push('Check all required fields are filled');
        suggestions.push('Ensure data format is correct');
      } else if (error.isServerError) {
        suggestions.push('Try again in a few minutes');
        suggestions.push('Contact support if the issue continues');
      }
    } else {
      suggestions.push('Try refreshing the page');
      suggestions.push('Contact support if the problem persists');
    }

    return suggestions;
  }

  // Check if error is validation error
  private isValidationError(error: ProcessableError): boolean {
    if (typeof error === 'string') {
      return false;
    }
    
    if (error instanceof ApiError) {
      return error.isValidationError;
    }

    return false;
  }

  // Show toast notification
  private showToastNotification(error: ProcessedError, duration?: number): void {
    const options = duration ? { duration } : {};

    switch (error.severity) {
      case ErrorSeverity.CRITICAL:
      case ErrorSeverity.HIGH:
        toast.error(error.userMessage, {
          ...options,
          description: error.suggestions[0],
          action: error.shouldRetry ? {
            label: 'Retry',
            onClick: () => window.location.reload(),
          } : undefined,
        });
        break;
      case ErrorSeverity.MEDIUM:
        toast.error(error.userMessage, {
          ...options,
          description: error.suggestions[0],
        });
        break;
      case ErrorSeverity.LOW:
        toast.warning(error.userMessage, options);
        break;
    }
  }

  // Log to console
  private logToConsole(error: ProcessedError): void {
    const logLevel = error.severity === ErrorSeverity.CRITICAL || error.severity === ErrorSeverity.HIGH 
      ? 'error' 
      : error.severity === ErrorSeverity.MEDIUM 
        ? 'warn' 
        : 'info';

    console[logLevel]('Error processed:', {
      id: error.id,
      type: error.type,
      severity: error.severity,
      message: error.technicalMessage,
      context: error.context,
      originalError: error.originalError,
    });
  }

  // Add error to queue
  private addToQueue(error: ProcessedError): void {
    this.errorQueue.unshift(error);
    
    if (this.errorQueue.length > this.maxQueueSize) {
      this.errorQueue = this.errorQueue.slice(0, this.maxQueueSize);
    }
  }

  // Report error to external service
  private async reportError(error: ProcessedError): Promise<void> {
    try {
      const report: ErrorReport = {
        id: error.id,
        timestamp: error.timestamp,
        type: error.type,
        severity: error.severity,
        message: error.technicalMessage,
        stack: error.originalError instanceof Error ? error.originalError.stack : undefined,
        context: error.context,
        userAgent: navigator.userAgent,
        url: window.location.href,
        userId: error.context?.userId || this.getCurrentUserId(),
        sessionId: error.context?.sessionId || this.getSessionId(),
      };

      // In production, send to your error reporting service
      if (import.meta.env.PROD) {
        // Example: await this.sendToSentry(report);
        // Example: await this.sendToLogRocket(report);
        console.log('Would report error:', report);
      } else {
        console.log('Error report (dev mode):', report);
      }
    } catch (reportingError) {
      console.warn('Failed to report error:', reportingError);
    }
  }

  // Generate unique error ID
  private generateErrorId(): string {
    return `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get current user ID
  private getCurrentUserId(): string | undefined {
    try {
      const authData = localStorage.getItem('auth-storage');
      if (authData) {
        const parsed = JSON.parse(authData);
        return parsed.state?.user?.id;
      }
    } catch (e) {
      // Ignore errors
    }
    return undefined;
  }

  // Get session ID
  private getSessionId(): string {
    let sessionId = sessionStorage.getItem('sessionId');
    if (!sessionId) {
      sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      sessionStorage.setItem('sessionId', sessionId);
    }
    return sessionId;
  }

  // Public methods for managing the service
  getErrorQueue(): ProcessedError[] {
    return [...this.errorQueue];
  }

  clearErrorQueue(): void {
    this.errorQueue = [];
  }

  setReportingEnabled(enabled: boolean): void {
    this.reportingEnabled = enabled;
  }

  isReportingEnabled(): boolean {
    return this.reportingEnabled;
  }

  // Export error queue for debugging
  exportErrors(): string {
    return JSON.stringify(this.errorQueue, null, 2);
  }
}

// Export singleton instance
export const errorService = ErrorService.getInstance();

// Convenience functions
export const handleError = (
  error: ProcessableError,
  context?: ErrorContext,
  options?: ErrorHandlerOptions
): ProcessedError => {
  return errorService.handleError(error, context, options);
};

export const processError = (
  error: ProcessableError,
  context?: ErrorContext
): ProcessedError => {
  return errorService.processError(error, context);
};